import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AnyPlaceBubble } from '~/stores/interfaces'
import { useBubblesStore } from '~/stores/bubbles'
import { formatType } from '~/utils/formatters'

// Define the specific main types this store will handle
const ALLOWED_MAIN_TYPES = ['bar', 'hotel', 'coffee', 'restaurant', 'place']

export const usePlaceStore = defineStore('place', () => {
  // Removed logger setup if not used elsewhere in the store
  // const { log, debug } = useLogger()

  const allPlaces = ref<AnyPlaceBubble[]>([])
  const selectedSubType = ref('All')
  const searchQuery = ref('')
  // Use the hardcoded list for available types
  const availableMainTypes = ref<string[]>([...ALLOWED_MAIN_TYPES])
  // Initially select all allowed types
  const selectedMainTypes = ref<string[]>([...ALLOWED_MAIN_TYPES])

  const bubblesStore = useBubblesStore()

  // Removed watch for bubblesStore.bubbleTypes as we use a fixed list now

  const formattedAvailableSubTypes = computed(() => {
    const relevantPlaces = allPlaces.value.filter((place) =>
      selectedMainTypes.value.includes(place.type)
    )
    const uniqueRawSubTypes = [
      ...new Set(relevantPlaces.flatMap((place) => place.types || []).filter(Boolean)),
    ]

    const formattedOptions = uniqueRawSubTypes.map((rawType) => ({
      raw: rawType,
      formatted: formatType(rawType),
    }))

    const allOption = { raw: 'All', formatted: 'All' }
    return relevantPlaces.length > 0 ? [allOption, ...formattedOptions] : []
  })

  const filteredPlaces = computed(() => {
    // Removed debug log
    const query = searchQuery.value.toLowerCase().trim()
    const results = allPlaces.value.filter((place) => {
      const mainTypeMatch = selectedMainTypes.value.includes(place.type)
      const subTypeMatch =
        selectedSubType.value === 'All' ||
        (place.types && place.types.includes(selectedSubType.value))
      const textMatch =
        query === '' ||
        (place.title?.toLowerCase().includes(query) ?? false) ||
        (place.description?.toLowerCase().includes(query) ?? false)

      // Removed detailed check log

      return mainTypeMatch && subTypeMatch && textMatch
    })
    // Removed debug log
    return results
  })

  const filteredPlacesWithLocation = computed(() => {
    return filteredPlaces.value.filter(
      (place) => place.location && place.location.lat && place.location.lng
    )
  })

  async function fetchAllRelevantPlaces() {
    const typesToFetch = availableMainTypes.value
    // Removed log
    setSelectedSubType('All')
    setSearchQuery('')

    const promises = typesToFetch.map((type) => {
      if (!bubblesStore.bubblesByType[type]) {
        return bubblesStore.fetchBubblesByType(type)
      }
      return Promise.resolve()
    })

    await Promise.all(promises)

    const combinedPlaces: AnyPlaceBubble[] = []
    typesToFetch.forEach((type) => {
      combinedPlaces.push(...((bubblesStore.bubblesByType[type] as AnyPlaceBubble[]) || []))
    })
    allPlaces.value = combinedPlaces
    // Removed log
    selectedMainTypes.value = [...typesToFetch]
  }

  function setSelectedSubType(type: string) {
    selectedSubType.value = type
  }

  function setSearchQuery(query: string) {
    searchQuery.value = query
  }

  function toggleMainType(type: string) {
    const index = selectedMainTypes.value.indexOf(type)
    if (index > -1) {
      selectedMainTypes.value.splice(index, 1)
    } else {
      selectedMainTypes.value.push(type)
    }
  }

  function removePlace(uuid: string) {
    allPlaces.value = allPlaces.value.filter((place) => place.uuid !== uuid)
  }

  return {
    searchQuery,
    availableMainTypes,
    selectedMainTypes,
    selectedSubType,
    filteredPlaces,
    filteredPlacesWithLocation,
    formattedAvailableSubTypes,
    setSearchQuery,
    setSelectedSubType,
    toggleMainType,
    fetchAllRelevantPlaces,
    removePlace,
  }
})
